/* =============================================================================
 *  Kreux Matrix — Sovereign Tech Stack Marquee
 *  Slots between Hero and OperationsLayer.
 *  Bilingual EN/DE via useLang() · 10 monochrome SVG logos ·
 *  seamless right→left infinite loop · pause on track hover · cyan neon
 *  glow on individual logo hover · prefers-reduced-motion respected.
 * ===========================================================================*/

import { useLang } from "@/lib/LangContext";

const dict = {
  en: {
    Header: "Engineered on Sovereign Enterprise Infrastructure.",
    Subtext:
      "100% GDPR-compliant AI integrations safely layered over your legacy systems with zero operational downtime."
  },
  de: {
    Header: "Entwickelt auf souveräner Enterprise-Infrastruktur.",
    Subtext:
      "100% DSGVO-konforme KI-Integrationen, nahtlos aufgesetzt auf Ihre bestehenden Systeme ohne operativen Ausfall."
  }
};

/* ─── Individual SVG logos ─────────────────────────────────────────────
 * Each uses currentColor so the parent wrapper drives idle/hover fill.
 * Consistent ~28px height, unified stroke/weight, kerning tuned per mark.
 * ─────────────────────────────────────────────────────────────────── */

/* Machine Labs — clean wordmark w/ minimal chevron mark */
const MachineLabs = (p) => (
  <svg viewBox="0 0 160 32" {...p}>
    <path d="M4 6 L14 16 L4 26" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    <text x="22" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="17" letterSpacing="0" fill="currentColor">Machine Labs</text>
  </svg>
);

/* OpenAI — hexagonal wordmark (stylized "O" as ring + wordmark) */
const OpenAI = (p) => (
  <svg viewBox="0 0 130 32" {...p}>
    <path d="M16 6 A10 10 0 1 1 15.99 6 Z M16 12 A4 4 0 1 1 15.99 12 Z" fill="currentColor" fillRule="evenodd" />
    <text x="32" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="18" letterSpacing="-0.3" fill="currentColor">OpenAI</text>
  </svg>
);

/* Anthropic (Claude) — iconic 3-blade starburst */
const Anthropic = (p) => (
  <svg viewBox="0 0 150 32" {...p}>
    <g transform="translate(0,0)">
      <path
        d="M16 4 L18.5 14 L28 16 L18.5 18 L16 28 L13.5 18 L4 16 L13.5 14 Z"
        fill="currentColor"
      />
    </g>
    <text x="36" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="17" letterSpacing="0" fill="currentColor">Anthropic</text>
  </svg>
);

/* ElevenLabs — twin-bar mark + wordmark */
const ElevenLabs = (p) => (
  <svg viewBox="0 0 155 32" {...p}>
    <rect x="4" y="6" width="5" height="20" rx="1.5" fill="currentColor" />
    <rect x="13" y="6" width="5" height="20" rx="1.5" fill="currentColor" />
    <text x="26" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="17" letterSpacing="-0.2" fill="currentColor">ElevenLabs</text>
  </svg>
);

/* Google Cloud (EU) — hex-cloud mark + wordmark */
const GoogleCloudEU = (p) => (
  <svg viewBox="0 0 180 32" {...p}>
    <path
      d="M10 20 A7 7 0 0 1 12 6.5 A8 8 0 0 1 27 8 A6 6 0 0 1 28 20 Z"
      fill="currentColor"
    />
    <text x="34" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="17" letterSpacing="-0.2" fill="currentColor">Google Cloud (EU)</text>
  </svg>
);

/* Hostinger — H bracket mark + wordmark */
const Hostinger = (p) => (
  <svg viewBox="0 0 135 32" {...p}>
    <path d="M4 6 L4 26 M14 6 L14 26 M4 16 L14 16" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" fill="none" />
    <text x="22" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="17" letterSpacing="-0.1" fill="currentColor">Hostinger EU</text>
  </svg>
);

/* n8n — three interconnected nodes (iconic) */
const N8n = (p) => (
  <svg viewBox="0 0 100 32" {...p}>
    <line x1="10" y1="16" x2="24" y2="16" stroke="currentColor" strokeWidth="2" />
    <line x1="30" y1="16" x2="44" y2="16" stroke="currentColor" strokeWidth="2" />
    <circle cx="6"  cy="16" r="4.5" fill="currentColor" />
    <circle cx="27" cy="16" r="4.5" fill="currentColor" />
    <circle cx="48" cy="16" r="4.5" fill="currentColor" />
    <text x="60" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="18" letterSpacing="0" fill="currentColor">n8n</text>
  </svg>
);

/* Microsoft — 4-square grid + wordmark */
const Microsoft = (p) => (
  <svg viewBox="0 0 140 32" {...p}>
    <rect x="2"  y="4"  width="10" height="10" fill="currentColor" />
    <rect x="14" y="4"  width="10" height="10" fill="currentColor" />
    <rect x="2"  y="16" width="10" height="10" fill="currentColor" />
    <rect x="14" y="16" width="10" height="10" fill="currentColor" />
    <text x="32" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="17" letterSpacing="-0.2" fill="currentColor">Microsoft</text>
  </svg>
);

/* SAP — chunky wordmark (only) */
const SAP = (p) => (
  <svg viewBox="0 0 60 32" {...p}>
    <text x="2" y="24" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="900" fontSize="24" letterSpacing="1" fill="currentColor">SAP</text>
  </svg>
);

/* CAS Software — angled bracket mark + wordmark */
const CASSoftware = (p) => (
  <svg viewBox="0 0 160 32" {...p}>
    <path d="M14 8 A8 8 0 1 0 14 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
    <text x="22" y="22" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif" fontWeight="700" fontSize="17" letterSpacing="0" fill="currentColor">CAS Software</text>
  </svg>
);

const LOGOS = [
  { name: "Machine Labs",     Comp: MachineLabs   },
  { name: "OpenAI",           Comp: OpenAI        },
  { name: "Anthropic Claude", Comp: Anthropic     },
  { name: "ElevenLabs",       Comp: ElevenLabs    },
  { name: "Google Cloud EU",  Comp: GoogleCloudEU },
  { name: "Hostinger EU",     Comp: Hostinger     },
  { name: "n8n",              Comp: N8n           },
  { name: "Microsoft",        Comp: Microsoft     },
  { name: "SAP",              Comp: SAP           },
  { name: "CAS Software",     Comp: CASSoftware   }
];

/* ─── Global background: Hex matrix (mirrors Hero / Advantage sections) ── */
const MarqueeHexMatrix = () => (
  <svg
    aria-hidden="true"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute inset-0"
  >
    <defs>
      <pattern id="kx-hex-msq" x="0" y="0" width="56" height="98" patternUnits="userSpaceOnUse">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M0 48 L0 80 L28 96 L56 80 L56 48"
          fill="none"
          stroke="rgba(255,255,255,0.03)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#kx-hex-msq)" />
  </svg>
);

/* ─── Global background: subtle drifting data nodes ────────────────────── */
const MarqueeDataNodes = () => {
  const nodes = [
    { top: "18%", left: "6%",  delay: "0s"   },
    { top: "26%", left: "92%", delay: "1.2s" },
    { top: "44%", left: "14%", delay: "2.6s" },
    { top: "58%", left: "88%", delay: "0.5s" },
    { top: "72%", left: "22%", delay: "1.9s" },
    { top: "34%", left: "68%", delay: "3.3s" },
    { top: "80%", left: "76%", delay: "0.9s" }
  ];
  return (
    <div aria-hidden="true" className="absolute inset-0">
      {nodes.map((n, i) => (
        <span
          key={i}
          className="msq-node absolute w-1 h-1 rounded-full bg-[var(--il-cyan)]/40"
          style={{
            top: n.top,
            left: n.left,
            animationDelay: n.delay,
            boxShadow: "0 0 8px rgba(0,229,255,0.5)"
          }}
        />
      ))}
    </div>
  );
};

const LogoCell = ({ Comp, name, idx }) => (
  <span
    data-testid={`marquee-logo-${idx}`}
    aria-label={name}
    className="msq-cell inline-flex items-center justify-center mx-10 shrink-0 opacity-40 grayscale hover:opacity-100 hover:grayscale-0 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_12px_rgba(0,255,255,0.6)] transition-all duration-300 ease-out"
  >
    <Comp height="28" width="auto" />
  </span>
);

const MarqueeStack = () => {
  const { lang } = useLang();
  const t = dict[lang] || dict.en;

  return (
    <section
      id="tech-stack"
      data-testid="tech-stack-marquee"
      className="relative w-full py-16 bg-transparent overflow-hidden font-sans"
      style={{
        "--il-cyan": "#00E5FF",
        color: "#FFFFFF"
      }}
    >
      <style>{styles}</style>

      {/* Global hexagon grid + subtle data nodes — mirrors Hero / Advantage layers */}
      <div aria-hidden="true" className="absolute inset-0 z-0 pointer-events-none opacity-70">
        <MarqueeHexMatrix />
        <MarqueeDataNodes />
      </div>

      <div className="relative z-10 max-w-[90rem] mx-auto">
        {/* Header */}
        <h2
          data-testid="marquee-header"
          className="text-white font-bold text-2xl tracking-wide text-center mb-2"
        >
          {t.Header}
        </h2>
        {/* Subtext */}
        <p
          data-testid="marquee-subtext"
          className="text-gray-400 text-sm max-w-2xl mx-auto text-center mb-10 px-4"
        >
          {t.Subtext}
        </p>

        {/* Marquee — hover on the group pauses the track; edge-fade via CSS mask */}
        <div
          className="msq-group relative flex w-full overflow-hidden [mask-image:_linear-gradient(to_right,transparent_0,_black_15%,_black_85%,transparent_100%)]"
          data-testid="marquee-group"
        >
          {/* Track — logos rendered TWICE for seamless -50% loop */}
          <div
            className="msq-track flex items-center whitespace-nowrap w-max"
            data-testid="marquee-track"
          >
            {LOGOS.map((L, i) => (
              <LogoCell key={`a-${i}`} idx={i} Comp={L.Comp} name={L.name} />
            ))}
            {LOGOS.map((L, i) => (
              <LogoCell key={`b-${i}`} idx={i + LOGOS.length} Comp={L.Comp} name={L.name} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = `
@keyframes msqScroll {
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-50%, 0, 0); }
}
.msq-track {
  animation: msqScroll 45s linear infinite;
  will-change: transform;
}
.msq-group:hover .msq-track {
  animation-play-state: paused;
}
/* Idle silver look — soft, unobtrusive; hover state defined via Tailwind on cell */
.msq-cell { color: #E2EAF0; }

/* Drifting data nodes — subtle breathing (mirrors FAQ / Advantage) */
@keyframes msqNodeDrift {
  0%, 100% { opacity: 0.25; transform: translateY(0) scale(1); }
  50%      { opacity: 0.75; transform: translateY(-4px) scale(1.4); }
}
.msq-node { animation: msqNodeDrift 5s ease-in-out infinite; will-change: opacity, transform; }

@media (prefers-reduced-motion: reduce) {
  .msq-track, .msq-node { animation: none !important; }
}
`;

export default MarqueeStack;
